import React, { useEffect, useRef, useState } from "react";
import { Card, FloatingLabel, Form, Table } from "react-bootstrap";
import SubmitButton from "../../shared/SubmitButton";
import { NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import ConfirmationModal from "../Confirmation";
import {
  appAbsPath,
  buttonLoadingClass,
  executeLaravelAPI,
  userToken,
} from "../../Utils";

const emptyForm = {
  id: null,
  page_url: "",
  meta_title: "",
  meta_description: "",
  canonical_url: "",
  json_schema: "",
  meta_keywords: "",
  is_indexable: true,
};

const SeoMetadataPage = () => {
  const buttonClass = useRef("theme-btn btn btn-success");
  const [buttonClassLoader, setButtonClass] = useState(buttonClass.current);
  const [form, setForm] = useState(emptyForm);
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);

  useEffect(() => {
    document.title = "SEO Metadata";
    fetchList();
  }, []);

  const fetchList = async () => {
    setLoadingList(true);
    try {
      const res = await executeLaravelAPI(
        "seo-metadata",
        { per_page: 50 },
        "GET",
        userToken,
        { raw: true },
      );

      if (res.ok && res.data?.status === 200) {
        const payload = res.data;
        // Laravel paginator is in payload.data, with records in payload.data.data
        const list =
          Array.isArray(payload?.data?.data)
            ? payload.data.data
            : Array.isArray(payload?.data)
              ? payload.data
              : [];
        setItems(list);
      } else {
        toast.error("Failed to load SEO metadata records.");
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load SEO metadata records.");
    } finally {
      setLoadingList(false);
    }
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handlePageUrlBlur = (e) => {
    const value = e.target.value;
    setForm((prev) => ({
      ...prev,
      canonical_url: value,
    }));
  };

  const onEdit = (record) => {
    setForm({
      id: record.id,
      page_url: record.page_url || "",
      meta_title: record.meta_title || "",
      meta_description: record.meta_description || "",
      canonical_url: record.canonical_url || "",
      json_schema: record.json_schema || "",
      meta_keywords: record.meta_keywords || "",
      is_indexable: record.is_indexable !== false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onReset = () => {
    setForm(emptyForm);
  };

  const confirmDelete = (record) => {
    setRecordToDelete(record);
    setShowConfirmModal(true);
  };

  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setRecordToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!recordToDelete) return;
    try {
      const res = await executeLaravelAPI(
        `seo-metadata/${recordToDelete.id}`,
        "",
        "DELETE",
        userToken,
        { raw: true },
      );

      if (res.ok && res.data?.status === 200) {
        toast.success("SEO metadata deleted successfully.");
        fetchList();
        if (form.id === recordToDelete.id) {
          onReset();
        }
        setShowConfirmModal(false);
        setRecordToDelete(null);
      } else {
        toast.error(
          res.data?.message || "Failed to delete SEO metadata record."
        );
      }
    } catch (e) {
      console.error(e);
      toast.error("Error deleting SEO metadata record.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.page_url.trim()) {
      toast.error("Page URL is required.");
      return;
    }
    if (!form.meta_title.trim()) {
      toast.error("Meta Title is required.");
      return;
    }
    if (!form.meta_description.trim()) {
      toast.error("Meta Description is required.");
      return;
    }
    if (!form.canonical_url.trim()) {
      toast.error("Canonical URL is required.");
      return;
    }
    if (!form.meta_keywords.trim()) {
      toast.error("Meta Keywords are required.");
      return;
    }
    if (!form.json_schema.trim()) {
      toast.error("JSON-LD Schema is required.");
      return;
    }
    try {
      setButtonClass(`${buttonClass.current} ${buttonLoadingClass}`);
      const payload = {
        page_url: form.page_url,
        meta_title: form.meta_title,
        meta_description: form.meta_description,
        canonical_url: form.canonical_url,
        json_schema: form.json_schema,
        meta_keywords: form.meta_keywords,
        is_indexable: form.is_indexable ? 1 : 0,
      };

      const isUpdate = Boolean(form.id);
      let res;
      if (isUpdate) {
        res = await executeLaravelAPI(
          `seo-metadata/${form.id}`,
          payload,
          "PUT",
          userToken,
          { raw: true },
        );
      } else {
        res = await executeLaravelAPI(
          "seo-metadata",
          payload,
          "POST",
          userToken,
          { raw: true },
        );
      }

      if (res.ok && res.data?.status === 200) {
        const actionLabel = isUpdate ? "updated" : "saved";
        toast.success(`SEO metadata ${actionLabel} successfully.`);
        onReset();
        fetchList();
      } else {
        const payloadData = res.data || {};

        // Try to extract Laravel validation errors first
        let message = "";
        if (payloadData.errors && typeof payloadData.errors === "object") {
          const firstFieldErrors = Object.values(payloadData.errors)[0];
          if (Array.isArray(firstFieldErrors) && firstFieldErrors.length > 0) {
            message = firstFieldErrors[0];
          }
        }

        if (!message && typeof payloadData.message === "string") {
          message = payloadData.message;
        }

        toast.error(
          message || "Failed to save SEO metadata. Please check your input."
        );
      }
    } catch (err) {
      console.error(err);
      toast.error("Error saving SEO metadata. Please try again.");
    } finally {
      setButtonClass(buttonClass.current);
    }
  };

  return (
    <div className="row">
      <div className="col-md-12 mb-3">
        <Card>
          <Card.Header>SEO Metadata</Card.Header>
          <Card.Body>
            <form onSubmit={handleSubmit} noValidate>
              <div className="row">
                <div className="col-md-12 mb-3">
                  <FloatingLabel controlId="page_url" label="Page URL (path or full URL)">
                    <Form.Control
                      type="text"
                      placeholder="/properties/buy/pune"
                      name="page_url"
                      value={form.page_url}
                      onChange={onChange}
                      onBlur={handlePageUrlBlur}
                    />
                  </FloatingLabel>
                </div>

                <div className="col-md-6 mb-3">
                  <FloatingLabel controlId="meta_title" label="Meta Title">
                    <Form.Control
                      type="text"
                      placeholder="Meta title"
                      name="meta_title"
                      value={form.meta_title}
                      onChange={onChange}
                    />
                  </FloatingLabel>
                </div>

                <div className="col-md-6 mb-3">
                  <FloatingLabel controlId="meta_description" label="Meta Description">
                    <Form.Control
                      type="text"
                      placeholder="Meta description"
                      name="meta_description"
                      value={form.meta_description}
                      onChange={onChange}
                    />
                  </FloatingLabel>
                </div>

                <div className="col-md-12 mb-3">
                  <FloatingLabel controlId="canonical_url" label="Canonical URL">
                    <Form.Control
                      type="text"
                      placeholder="https://www.example.com/..."
                      name="canonical_url"
                      value={form.canonical_url}
                      onChange={onChange}
                      disabled
                    />
                  </FloatingLabel>
                </div>

                <div className="col-md-12 mb-3">
                  <FloatingLabel controlId="meta_keywords" label="Meta Keywords (comma separated)">
                    <Form.Control
                      type="text"
                      placeholder="keyword1, keyword2"
                      name="meta_keywords"
                      value={form.meta_keywords}
                      onChange={onChange}
                    />
                  </FloatingLabel>
                </div>

                <div className="col-md-12 mb-3">
                  <FloatingLabel controlId="json_schema" label="JSON-LD Schema">
                    <Form.Control
                      as="textarea"
                      rows={5}
                      placeholder='{"@context": "https://schema.org", "@type": "WebPage"}'
                      name="json_schema"
                      value={form.json_schema}
                      onChange={onChange}
                    />
                  </FloatingLabel>
                </div>

                <div className="col-md-6 mb-3 d-flex align-items-center">
                  <Form.Check
                    type="switch"
                    id="is_indexable"
                    label="Allow indexing (index / noindex)"
                    name="is_indexable"
                    checked={form.is_indexable}
                    onChange={onChange}
                  />
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2">
                <NavLink
                  className="theme-btn secondary me-2 cancel-btn btn btn-outline-secondary"
                  to={`${appAbsPath}/portaladmin/dashboard`}
                  onClick={onReset}
                >
                  Cancel
                </NavLink>
                <SubmitButton
                  type="submit"
                  className={buttonClassLoader}
                  label={form.id ? "Update" : "Save"}
                />
              </div>
            </form>
          </Card.Body>
        </Card>
      </div>

      <div className="col-md-12">
        <Card>
          <Card.Header>Existing SEO Records</Card.Header>
          <Card.Body>
            {loadingList ? (
              <p>Loading...</p>
            ) : items.length === 0 ? (
              <p className="text-muted mb-0">No SEO metadata records found.</p>
            ) : (
              <div className="table-responsive">
                <Table striped hover size="sm">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Page URL</th>
                      <th>Meta Title</th>
                      <th>Indexable</th>
                      <th>Updated</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row, idx) => (
                      <tr key={row.id}>
                        <td>{idx + 1}</td>
                        <td>{row.page_url}</td>
                        <td>{row.meta_title}</td>
                        <td>{row.is_indexable ? "Yes" : "No"}</td>
                        <td>{row.updated_at}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 me-2"
                            onClick={() => onEdit(row)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-danger p-0"
                            onClick={() => confirmDelete(row)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>

      <ConfirmationModal
        show={showConfirmModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        modelBody={
          recordToDelete
            ? `Are you sure you want to delete SEO metadata for "${recordToDelete.page_url}"?`
            : "Are you sure you want to delete this SEO metadata record?"
        }
      />
    </div>
  );
};

export default SeoMetadataPage;

